import { useCallback, useState } from 'react';

import { api } from '@/lib/api';
import { fanficsCatalogApi } from '@/lib/fanfics-catalog-api';
import { resolveNestedFandomId } from '@/features/seo-listing/utils/resolve-nested-fandom-id.util';
import { resolvePermanentUrl } from '@/features/seo-listing/utils/resolve-permanent-url';

export interface Character {
  id: number;
  name: string;
  fanfic_cnt: number;
  fandom_title: string;
  permanentUrl?: string | null;
  fandomId?: number | string;
  fandom_id?: number | string;
  fandom?: { id?: number | string } | number | string | null;
}

interface CharacterSearchResult {
  characters: Character[];
  hasNextPage: boolean;
  hasPrevPage: boolean;
  limit: number;
  nextPage: number | null;
  page: number;
  totalDocs: number;
  totalPages: number;
}

interface UseCharactersOptions {
  useNewApi?: boolean;
  // Scopes fanfic_cnt to fanfic or original works (required for listing-accurate counts)
  type?: 'fanfic' | 'original';
}

/** Paginated result for infinite scroll dropdowns */
export interface PaginatedCharacterResult {
  items: {
    id: string;
    name: string;
    count: number;
    fandomTitle?: string;
    permanentUrl?: string;
    fandomId?: string;
  }[];
  hasNextPage: boolean;
}

interface CharacterSearchItem {
  id: string;
  name: string;
  count: number;
  fandomTitle?: string;
  fandom_title?: string;
  permanentUrl?: string;
  fandomId?: string;
}

const mapCharacterSearchItem = (character: Character): CharacterSearchItem => {
  const id = character.id.toString();
  const fandomId = resolveNestedFandomId(character);
  return {
    id,
    name: character.name,
    count: character.fanfic_cnt || 0,
    fandomTitle: character.fandom_title,
    fandom_title: character.fandom_title,
    permanentUrl: resolvePermanentUrl(character.permanentUrl),
    fandomId,
  };
};

const DROPDOWN_LIMIT = 50;

/** Build fandom query string: &fandom=1&fandom=2 or empty string.
 * Uses work-level `fandom=` (same as fanfics-custom/v2) so fanfic_cnt matches the listing. */
const buildFandomIdsParam = (fandomIds?: string[]): string => {
  if (!fandomIds || fandomIds.length === 0) return '';
  return fandomIds.map((fandomId) => `&fandom=${fandomId}`).join('');
};

/** Build universeIds query string: &universeId=1&universeId=2 or empty string */
const buildUniverseIdsParam = (universeIds?: string[]): string => {
  if (!universeIds || universeIds.length === 0) return '';
  return universeIds.map((universeId) => `&universeId=${universeId}`).join('');
};

const buildTypeParam = (catalogType?: 'fanfic' | 'original'): string =>
  catalogType ? `&type=${catalogType}` : '';

export function useCharacters(options: UseCharactersOptions = {}) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const useNewApi = options.useNewApi ?? false;
  // Catalog listing always scopes counts to fanfic|original; default fanfic when
  // useNewApi is on so FanfictionFilters search matches the grid without extra wiring.
  const catalogType = options.type ?? (useNewApi ? 'fanfic' : undefined);

  const searchCharacters = useCallback(
    async (
      query: string,
      limit: number = 10,
    ): Promise<
      {
        id: string;
        name: string;
        count: number;
        fandom_title?: string;
        permanentUrl?: string;
        fandomId?: string;
      }[]
    > => {
      if (!query.trim()) return [];

      setLoading(true);
      setError(null);

      try {
        const encodedQuery = encodeURIComponent(query);
        const typeParam = buildTypeParam(catalogType);
        const response = await (useNewApi ? fanficsCatalogApi : api).get<CharacterSearchResult>(
          `/api/characters-custom?where[name][like]=${encodedQuery}&limit=${limit}${typeParam}`,
        );
        const results = response.data.characters.map(mapCharacterSearchItem);

        return results;
      } catch (err) {
        console.error('Error searching characters:', err);
        setError(err instanceof Error ? err.message : 'Failed to search characters');
        return [];
      } finally {
        setLoading(false);
      }
    },
    [useNewApi, catalogType],
  );

  /** Paginated search for infinite scroll dropdowns with optional fandom filter */
  const searchCharactersPaginated = useCallback(
    async (
      query: string,
      fandomIds?: string[],
      page: number = 1,
      universeIds?: string[],
    ): Promise<PaginatedCharacterResult> => {
      if (!query.trim()) return { items: [], hasNextPage: false };

      try {
        const encodedQuery = encodeURIComponent(query);
        const fandomParam = buildFandomIdsParam(fandomIds);
        const universeParam = buildUniverseIdsParam(universeIds);
        const typeParam = buildTypeParam(catalogType);
        const response = await (useNewApi ? fanficsCatalogApi : api).get<CharacterSearchResult>(
          `/api/characters-custom?where[name][like]=${encodedQuery}&page=${page}&limit=${DROPDOWN_LIMIT}${fandomParam}${universeParam}${typeParam}`,
        );
        const items = response.data.characters.map(mapCharacterSearchItem);

        return { items, hasNextPage: response.data.hasNextPage };
      } catch (err) {
        console.error('Error searching characters paginated:', err);
        return { items: [], hasNextPage: false };
      }
    },
    [useNewApi, catalogType],
  );

  const getCharacters = useCallback(
    async (page: number = 1, limit: number = 20): Promise<CharacterSearchResult> => {
      setLoading(true);
      setError(null);

      try {
        const typeParam = buildTypeParam(catalogType);
        const response = await (useNewApi ? fanficsCatalogApi : api).get<CharacterSearchResult>(
          `/api/characters-custom?page=${page}&limit=${limit}${typeParam}`,
        );

        return response.data;
      } catch (err) {
        console.error('Error fetching characters:', err);
        setError(err instanceof Error ? err.message : 'Failed to fetch characters');
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [useNewApi, catalogType],
  );

  /** Get characters with paginated result for infinite scroll dropdowns */
  const getCharactersPaginated = useCallback(
    async (
      page: number = 1,
      fandomIds?: string[],
      universeIds?: string[],
    ): Promise<PaginatedCharacterResult> => {
      try {
        const fandomParam = buildFandomIdsParam(fandomIds);
        const universeParam = buildUniverseIdsParam(universeIds);
        const typeParam = buildTypeParam(catalogType);
        const response = await (useNewApi ? fanficsCatalogApi : api).get<CharacterSearchResult>(
          `/api/characters-custom?page=${page}&limit=${DROPDOWN_LIMIT}${fandomParam}${universeParam}${typeParam}`,
        );
        const items = response.data.characters.map(mapCharacterSearchItem);

        return { items, hasNextPage: response.data.hasNextPage };
      } catch (err) {
        console.error('Error fetching characters paginated:', err);
        return { items: [], hasNextPage: false };
      }
    },
    [useNewApi, catalogType],
  );

  return {
    searchCharacters,
    searchCharactersPaginated,
    getCharacters,
    getCharactersPaginated,
    loading,
    error,
  };
}
